import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Dialog, Tooltip as MuiTooltip } from "@mui/material";
import CreatableSelect from "react-select/creatable";
import AlertDialog from "../../components/shared/AlertDialog";
import { FileDropzone } from "../../components/shared/FileDropzone";
import {
  Input,
  Label,
  CheckBoxWithLabel,
} from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  getGrantDocuments,
  uploadGrantDocument,
  updateGrantDocument,
  deleteGrantDocuments,
  getGrantTransactionsForDocument,
  IGrantDocument,
  IGrantTransactionOption,
} from "../../api/grantDocumentsApi";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { NewCollapsibleComponent as CollapsibleComponent } from "../../components/shared/NewCollapsibleComponent";
import { formatDisplayDate } from "../../utils/date";
import { Grant } from "../../types/Grant";
import { convertFileToBase64, downloadS3File } from "../../utils/DownloadFile";
import StatusLabel from "./StatusLabel";
import { DocumentType } from "../../types/DocumentsInterface";

const DOCUMENT_TYPES_BY_CONTEXT = {
  grant: [
    {
      label: "Grant Related Document",
      value: DocumentType.GRANT_RELATED_DOCUMENT,
    },
  ],
  employee: [
    {
      label: "Employee Related Document",
      value: DocumentType.EMPLOYEE_RELATED_DOCUMENT,
    },
  ],
  transaction: [
    {
      label: "Grant Transaction Document",
      value: DocumentType.GRANT_TRANSACTION_DOCUMENT,
    },
  ],
};

type UploadContext = "grant" | "employee" | "transaction";
type RightPanelMode = "preview" | "upload" | "edit";

// ─── Tooltip descriptions ──────────────────────────────────────────────────────

const splitText =
  "Document issued when a stock split occurs, detailing the adjustment to the number of options and exercise price to reflect the split ratio.";
const grantText =
  "Official document confirming the equity grant details including the number of options granted, vesting schedule, and exercise price.";

const getGrantLetterName = (grant: Grant) =>
  `${grant.employeeName || "employee"}_${
    grant.grantIdentifier || grant.optionHolderId
  }`.replace(/[\\/:*?"<>|]/g, "_");

const getDocumentName = (doc: IGrantDocument, grant: Grant) => {
  if (doc.documentType === DocumentType.GRANT_LETTER) {
    return getGrantLetterName(grant);
  }

  return doc.documentName || (doc as any).name || (doc as any).fileName || "";
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function GrantDocumentDialog({
  grant,
  onClose,
}: {
  grant: Grant;
  onClose: () => void;
}) {
  const previewRef = React.useRef<HTMLDivElement>(null);

  // Document state
  const [documents, setDocuments] = useState<IGrantDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<IGrantDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Right panel
  const [rightPanelMode, setRightPanelMode] =
    useState<RightPanelMode>("preview");
  const [uploadContext, setUploadContext] = useState<UploadContext>("grant");
  const [editingDoc, setEditingDoc] = useState<IGrantDocument | null>(null);
  const isEditingSystemGenerated = Boolean(
    editingDoc && !editingDoc.isUserUploaded
  );

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    docId?: string;
  }>({ open: false });

  // Accordion state
  const [accordionOpened, setAccordionOpened] = useState<{
    [key: string]: boolean;
  }>({
    grantLevel: true,
    employeeLevel: true,
    transactionDocs: true,
  });

  const [grantTransactions, setGrantTransactions] = useState<
    IGrantTransactionOption[]
  >([]);

  useEffect(() => {
    if (grant?.optionHolderId) {
      getGrantTransactionsForDocument(grant.optionHolderId)
        .then((txs) => setGrantTransactions(txs))
        .catch((err) => toast.error("Failed to fetch grant transactions"));
    }
  }, [grant?.optionHolderId]);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    let isMounted = true;
    setIsLoading(true);
    try {
      const docs = await getGrantDocuments(
        `${grant.optionHolderId},${grant.employeeId}`
      );
      if (isMounted) {
        setDocuments(docs);
        setSelectedDoc(docs.length > 0 ? docs[0] : null);
        const docType = docs[0]?.documentType;
        if (docType === DocumentType.EMPLOYEE_RELATED_DOCUMENT) {
          setAccordionOpened((prev) => ({
            ...prev,
            employeeLevel: false,
          }));
        } else if (docType === DocumentType.GRANT_TRANSACTION_DOCUMENT) {
          setAccordionOpened((prev) => ({
            ...prev,
            transactionDocs: false,
          }));
        } else {
          setAccordionOpened((prev) => ({
            ...prev,
            grantLevel: false,
          }));
        }
      }
    } catch {
      toast("Failed to fetch documents", { type: "error" });
    } finally {
      if (isMounted) setIsLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [grant]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);
  const grantDocs = documents.filter(
    (d) =>
      d.documentType === DocumentType.GRANT_RELATED_DOCUMENT ||
      d.documentType === DocumentType.GRANT_LETTER ||
      d.documentType === DocumentType.SPLIT_LETTER ||
      d.documentType === DocumentType.BONUS_LETTER ||
      (d.entityType?.toLowerCase() === "grant" &&
        d.documentType !== DocumentType.EMPLOYEE_RELATED_DOCUMENT &&
        d.documentType !== DocumentType.GRANT_TRANSACTION_DOCUMENT)
  );
  const employeeDocs = documents.filter(
    (d) =>
      d.documentType === DocumentType.EMPLOYEE_RELATED_DOCUMENT ||
      (d.entityType?.toLowerCase() === "employee" &&
        d.documentType !== DocumentType.GRANT_RELATED_DOCUMENT &&
        d.documentType !== DocumentType.GRANT_TRANSACTION_DOCUMENT)
  );
  const transactionDocs = documents.filter(
    (d) =>
      d.documentType === DocumentType.GRANT_TRANSACTION_DOCUMENT ||
      (d.entityType?.toLowerCase() === "transaction" &&
        d.documentType !== DocumentType.GRANT_RELATED_DOCUMENT &&
        d.documentType !== DocumentType.EMPLOYEE_RELATED_DOCUMENT)
  );

  const handleAccordionClick = (section: string) => {
    setAccordionOpened((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleAddClick = (context: UploadContext) => {
    setEditingDoc(null);
    setUploadContext(context);
    const targetDocType = DOCUMENT_TYPES_BY_CONTEXT[context][0].value;
    uploadFormik.resetForm({
      values: {
        name: "",
        documentType: targetDocType,
        transactionId: "",
        shownToEmployee: false,
        canEmployeeDownload: false,
        tags: [],
        file: null,
      },
    });
    setRightPanelMode("upload");
    setSelectedDoc(null);
  };

  const handleEditClick = (doc: IGrantDocument) => {
    setEditingDoc(doc);
    setUploadContext(
      doc.documentType === DocumentType.EMPLOYEE_RELATED_DOCUMENT ||
        doc.entityType === "employee"
        ? "employee"
        : doc.documentType === DocumentType.GRANT_TRANSACTION_DOCUMENT ||
          doc.entityType === "transaction"
        ? "transaction"
        : "grant"
    );
    uploadFormik.resetForm({
      values: {
        name:
          doc.documentName || (doc as any).name || (doc as any).fileName || "",
        documentType: doc.documentType || "",
        transactionId: (doc.metaData as any)?.transactionId || "",
        shownToEmployee: doc.metaData?.shownToEmployee || false,
        canEmployeeDownload: doc.metaData?.canEmployeeDownload || false,
        tags: doc.metaData?.tags || [],
        file: null,
      },
    });
    setRightPanelMode("edit");
    setSelectedDoc(null);
  };

  const handleDeleteClick = (docId: string | undefined) => {
    if (!docId) return;
    setDeleteDialog({ open: true, docId });
  };

  const confirmDelete = async () => {
    const docId = deleteDialog.docId;
    if (!docId) return;
    setIsLoading(true);
    try {
      await deleteGrantDocuments([docId]);
      toast.success("Document deleted successfully");
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      setDeleteDialog({ open: false });
    } catch {
      toast.error("Failed to delete document");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = (doc: IGrantDocument) => {
    if (doc.s3Url) {
      const fileName =
        doc.documentType === DocumentType.GRANT_LETTER
          ? `${getGrantLetterName(grant)}.pdf`
          : getDocumentName(doc, grant) || "document";

      downloadS3File(doc.s3Url, fileName, false);
    } else {
      toast("No URL available for download", { type: "error" });
    }
  };

  // ─── Upload / Edit formik ────────────────────────────────────────────────────

  const uploadFormik = useFormik({
    initialValues: {
      name: "",
      documentType: DocumentType.GRANT_RELATED_DOCUMENT as string,
      transactionId: "",
      shownToEmployee: false,
      canEmployeeDownload: false,
      tags: [] as { label: string; value: string }[],
      file: null as File | null,
    },
    validationSchema: Yup.object({
      name: isEditingSystemGenerated
        ? Yup.string()
        : Yup.string().required("Document Name is required"),
      documentType: isEditingSystemGenerated
        ? Yup.string()
        : Yup.string().required("Document Type is required"),
      transactionId: Yup.string().when([], {
        is: () => uploadContext === "transaction" && !isEditingSystemGenerated,
        then: (schema) =>
          schema.required("Related Grant Transaction is required"),
      }),
      file: editingDoc ? Yup.mixed() : Yup.mixed().required("File is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (
        uploadContext === "transaction" &&
        !isEditingSystemGenerated &&
        !values.transactionId
      ) {
        uploadFormik.setFieldTouched("transactionId", true);
        toast.error("Please select a related grant transaction.");
        return;
      }
      setIsLoading(true);
      try {
        if (editingDoc) {
          if (isEditingSystemGenerated) {
            await updateGrantDocument({
              id: editingDoc.id!,
              metaData: {
                ...editingDoc.metaData,
                shownToEmployee: values.shownToEmployee,
                canEmployeeDownload: values.shownToEmployee
                  ? values.canEmployeeDownload
                  : false,
                tags: values.tags.map(({ label, value }) => ({ label, value })),
              },
            });
          } else {
            await updateGrantDocument({
              id: editingDoc.id!,
              documentType: values.documentType,
              documentName: values.name,
              metaData: {
                shownToEmployee: values.shownToEmployee,
                canEmployeeDownload: values.shownToEmployee
                  ? values.canEmployeeDownload
                  : false,
                tags: values.tags || [],
                ...(values.transactionId && {
                  transactionId: values.transactionId,
                }),
              },
            });
          }
          toast.success("Document updated successfully!");
        } else {
          if (!values.file) return;
          const base64String = await convertFileToBase64(values.file);
          const base64Data =
            (base64String as string).split(",")[1] || (base64String as string);
          const targetEntityId =
            uploadContext === "employee"
              ? grant.employeeId
              : grant.optionHolderId;

          await uploadGrantDocument({
            entityId: targetEntityId,
            documentType: values.documentType,
            documentName: values.name,
            b64File: base64Data,
            contentType: values.file.type,
            metaData: {
              shownToEmployee: values.shownToEmployee,
              canEmployeeDownload: values.canEmployeeDownload,
              tags: values.tags || [],
              ...(values.transactionId && {
                transactionId: values.transactionId,
              }),
            },
          });
          toast.success("Document uploaded successfully!");
        }
        resetForm();
        setEditingDoc(null);
        setRightPanelMode("preview");
        await fetchDocuments();
      } catch {
        toast.error(
          editingDoc ? "Failed to update document" : "Failed to upload document"
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // ─── Render helpers ──────────────────────────────────────────────────────────

  const renderDocCard = (doc: IGrantDocument, isSystemGenerated = false) => {
    const isSelected = selectedDoc?.id === doc.id;
    const docTitle = getDocumentName(doc, grant) || getGrantLetterName(grant);
    const selectDocument = () => {
      setSelectedDoc(doc);
      setRightPanelMode("preview");
    };
    return (
      <div
        key={doc.id}
        role="button"
        tabIndex={0}
        aria-label={`Preview ${docTitle}`}
        className={`p-4 cursor-pointer transition-all duration-200 rounded-lg border group ${
          isSelected
            ? "bg-orange-50 border-orange-200"
            : "bg-white border-gray-200 hover:bg-gray-50"
        } focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary`}
        onClick={selectDocument}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault();
            selectDocument();
          }
        }}
      >
        <div className="flex items-start gap-3">
          <VStack className="flex-1 min-w-0">
            <HStack className="justify-between items-center">
              <HStack className="items-center gap-1 min-w-0">
                <span className="font-medium text-gray-900 text-sm truncate">
                  {docTitle.includes("Split") ? "Split Letter" : docTitle}
                </span>
                {isSystemGenerated && (
                  <MuiTooltip
                    title={docTitle.includes("Split") ? splitText : grantText}
                    placement="right"
                    arrow
                    PopperProps={{
                      style: { zIndex: 1600 },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          options: { boundary: "viewport", padding: 16 },
                        },
                      ],
                    }}
                    slotProps={{
                      tooltip: {
                        className:
                          "!bg-slate-light !text-primaryText !text-sm !shadow-lg",
                      },
                      arrow: { className: "!text-slate-light" },
                    }}
                  >
                    <button
                      type="button"
                      className="inline-flex cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                      aria-label={`About ${docTitle}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        width={14}
                        aria-hidden="true"
                      />
                    </button>
                  </MuiTooltip>
                )}
              </HStack>

              <HStack className="items-center gap-2 flex-shrink-0 pl-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {doc.generatedOn ? formatDisplayDate(doc.generatedOn) : ""}
                </span>
                <HStack className="opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(doc);
                    }}
                    className="p-1 text-gray-400 hover:text-orange-500"
                    title="Edit Document"
                    aria-label={
                      isSystemGenerated ? `Edit ${docTitle}` : undefined
                    }
                  >
                    <Icon icon="mdi:pencil" width={14} />
                  </button>
                  {!isSystemGenerated && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(doc.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Delete Document"
                    >
                      <Icon icon="mdi:delete" width={14} />
                    </button>
                  )}
                </HStack>
              </HStack>
            </HStack>

            <HStack className="items-center gap-2 mt-1">
              <Icon
                icon="vscode-icons:file-type-pdf2"
                width={16}
                className="text-gray-500"
              />
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                {doc.isUserUploaded ? "User-Uploaded" : "System-Generated"}
              </span>
              {doc.metaData?.shownToEmployee && (
                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                  Visible to Employee
                </span>
              )}
            </HStack>
          </VStack>
        </div>
      </div>
    );
  };

  const renderAddButton = (context: UploadContext) => (
    <button
      onClick={() => handleAddClick(context)}
      className="mt-2 w-full flex items-center justify-center gap-1 py-2 text-xs text-orange-600 hover:text-orange-700 border border-dashed border-orange-300 hover:border-orange-400 rounded-lg transition-colors"
    >
      <Icon icon="mdi:plus" width={14} />
      Add Document
    </button>
  );

  const renderPreview = () => {
    if (!selectedDoc) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-4">
          <Icon
            icon="mdi:file-document-outline"
            width={64}
            className="text-gray-300"
          />
          <p>Select a document to preview</p>
        </div>
      );
    }

    if (
      selectedDoc.documentType === DocumentType.GRANT_LETTER ||
      selectedDoc.type === "application/pdf" ||
      selectedDoc.s3Url?.toLowerCase().endsWith(".pdf")
    ) {
      return (
        <LazyPdfDocument
          url={selectedDoc.s3Url || ""}
          name={getDocumentName(selectedDoc, grant)}
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Icon icon="mdi:file-download" width={64} className="text-gray-400" />
        <p className="text-gray-600">
          Preview not available for this file type
        </p>
        <ButtonPrimary onClick={() => downloadFile(selectedDoc)}>
          Download Document
        </ButtonPrimary>
      </div>
    );
  };

  const renderUploadForm = () => {
    const isEditMode = rightPanelMode === "edit";
    const docTypes = DOCUMENT_TYPES_BY_CONTEXT[uploadContext];

    return (
      <VStack className="w-full px-8 gap-6 py-6 h-full overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800">
            {isEditMode ? "Edit Document" : "Upload New Document"}
          </h3>
          <button
            onClick={() => {
              uploadFormik.resetForm();
              setEditingDoc(null);
              setRightPanelMode("preview");
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon icon="mdi:close" width={18} />
          </button>
        </div>

        <form onSubmit={uploadFormik.handleSubmit} className="contents">
          {/* Document Type */}
          {!isEditingSystemGenerated && (
            <div>
              <Label className="required">Document Type</Label>
              <Select
                options={docTypes}
                textGetter={(v: any) => v.label}
                valueGetter={(v: any) => v.value}
                onChange={(e: any) =>
                  uploadFormik.setFieldValue(
                    "documentType",
                    e.target ? e.target.value : e
                  )
                }
                placeholder="Select Document Type"
                className="mt-1"
                value={uploadFormik.values.documentType}
              />
              {uploadFormik.touched.documentType &&
                uploadFormik.errors.documentType && (
                  <Error text={uploadFormik.errors.documentType as string} />
                )}
            </div>
          )}

          {/* Related Transaction selector when uploading transaction docs */}
          {uploadContext === "transaction" && !isEditingSystemGenerated && (
            <div>
              <Label className="required">Related Grant Transaction</Label>
              <Select
                options={grantTransactions}
                textGetter={(v: any) => v.label}
                valueGetter={(v: any) => v.id}
                onChange={(e: any) => {
                  const val = e.target ? e.target.value : e;
                  uploadFormik.setFieldValue("transactionId", val);
                  const selectedTx = grantTransactions.find(
                    (t) => t.id === val
                  );
                  if (selectedTx && !uploadFormik.values.name) {
                    uploadFormik.setFieldValue("name", selectedTx.label);
                  }
                }}
                placeholder="Select Grant Transaction (Stock Exercise, Cashout, Sold...)"
                className="mt-1"
                value={uploadFormik.values.transactionId}
                onBlur={() =>
                  uploadFormik.setFieldTouched("transactionId", true)
                }
              />
              {uploadFormik.touched.transactionId &&
                uploadFormik.errors.transactionId && (
                  <Error text={uploadFormik.errors.transactionId as string} />
                )}
            </div>
          )}

          {/* Document Name */}
          {isEditingSystemGenerated ? (
            <VStack className="gap-2">
              <span className="text-xs2 font-medium text-primaryText">
                Document name
              </span>
              <p className="rounded-lg border border-inputBorder bg-tertiaryBg p-3 text-sm text-primaryText break-words">
                {uploadFormik.values.name || "Untitled document"}
              </p>
            </VStack>
          ) : (
            <div>
              <Label className="required">Document Name</Label>
              <Input
                placeholder="e.g. Employment Agreement 2024"
                {...uploadFormik.getFieldProps("name")}
              />
              {uploadFormik.touched.name && uploadFormik.errors.name && (
                <Error text={uploadFormik.errors.name as string} />
              )}
            </div>
          )}

          {/* File */}
          {!isEditingSystemGenerated && (
            <div>
              <Label className={editingDoc ? "" : "required"}>File</Label>
              <div className="mt-1">
                <FileDropzone
                  instructionText={
                    isEditMode
                      ? "File updates are not allowed in edit mode."
                      : "Upload PDF files only (.pdf) — max 2 MB"
                  }
                  acceptedFileExtensions={[".pdf"]}
                  onFileSelect={(file) => {
                    if (file && file.size > 2 * 1024 * 1024) {
                      toast.error("File size exceeds the 2 MB limit.");
                      return;
                    }
                    uploadFormik.setFieldValue("file", file);
                    if (!uploadFormik.values.name) {
                      uploadFormik.setFieldValue(
                        "name",
                        file.name.split(".")[0]
                      );
                    }
                  }}
                  onClear={() => uploadFormik.setFieldValue("file", null)}
                  selectedFile={
                    isEditMode && editingDoc?.documentName
                      ? ({ name: editingDoc.documentName } as File)
                      : uploadFormik.values.file || undefined
                  }
                  disabled={isEditMode}
                />
                {uploadFormik.touched.file && uploadFormik.errors.file && (
                  <Error text={uploadFormik.errors.file as string} />
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <Label
              htmlFor={
                isEditingSystemGenerated ? "grant-document-tags" : undefined
              }
            >
              Tags
            </Label>
            <CreatableSelect
              inputId={
                isEditingSystemGenerated ? "grant-document-tags" : undefined
              }
              isMulti
              options={[]}
              value={uploadFormik.values.tags}
              onChange={(selected: any) =>
                uploadFormik.setFieldValue("tags", selected || [])
              }
              placeholder="Type and press enter to add tags"
            />
          </div>

          {/* Employee visibility */}
          <HStack className="items-center gap-6">
            <CheckBoxWithLabel
              label="Show document to Employee"
              labelclassname="ml-2"
              id={
                isEditingSystemGenerated
                  ? "grant-document-shown-to-employee"
                  : undefined
              }
              name="shownToEmployee"
              checked={uploadFormik.values.shownToEmployee}
              onChange={(e: any) =>
                uploadFormik.setFieldValue("shownToEmployee", e.target.checked)
              }
              className="text-white accent-orange-501 w-4 h-4"
            />
            {uploadFormik.values.shownToEmployee && (
              <CheckBoxWithLabel
                label="Allow Employee to Download"
                labelclassname="ml-2"
                id={
                  isEditingSystemGenerated
                    ? "grant-document-can-employee-download"
                    : undefined
                }
                name="canEmployeeDownload"
                checked={uploadFormik.values.canEmployeeDownload}
                onChange={(e: any) =>
                  uploadFormik.setFieldValue(
                    "canEmployeeDownload",
                    e.target.checked
                  )
                }
                className="text-white accent-orange-501 w-4 h-4"
              />
            )}
          </HStack>

          {/* Actions */}
          <HStack className="justify-between pt-2">
            <ButtonSecondary
              type="button"
              onClick={() => {
                uploadFormik.resetForm();
                setEditingDoc(null);
                setRightPanelMode("preview");
              }}
            >
              Cancel
            </ButtonSecondary>
            <ButtonPrimary
              type="submit"
              disabled={isLoading || !uploadFormik.dirty}
              // @ts-ignore
              loading={isLoading}
            >
              {isEditMode ? "Save Changes" : "Upload"}
            </ButtonPrimary>
          </HStack>
        </form>
      </VStack>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────────────

  return (
    <VStack className="flex w-full h-[700px] bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <VStack className="flex flex-col gap-2 w-full border-b border-gray-200">
        <VStack className="p-8 pb-4 text-lg font-medium gap-1">
          <h6 className="flex justify-between">
            Grant Documents — {grant.employeeName} · Grant ID:{" "}
            {grant.grantIdentifier}
            <span className="cursor-pointer" onClick={() => onClose()}>
              <Icon icon="mdi:close" width={20} />
            </span>
          </h6>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>Grant Date: {formatDisplayDate(grant.grantDate)}</span>
            <span>·</span>
            <span className="px-2 py-1 rounded text-xs font-medium">
              <StatusLabel
                state={grant.optionHolderState}
                _className="text-[12px] p-1"
              />
            </span>
            {grant.grantLetterTemplateName && (
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                Template: {grant.grantLetterTemplateName}
              </span>
            )}
            {documents.length > 0 &&
              documents.some((doc) => doc.documentType === "SPLIT_LETTER") && (
                <span className="px-2 py-1 bg-[#003366] text-white rounded text-xs font-medium">
                  Split applied
                </span>
              )}
          </div>
        </VStack>
      </VStack>

      <HStack className="flex-1 min-h-0">
        {/* ── Left panel: accordion list ── */}
        <div className="w-[40%] border-r border-gray-200 flex flex-col h-full">
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <VStack className="gap-y-4">
              {/* Grant-Level Documents */}
              <CollapsibleComponent
                label="Grant-Level Documents"
                labelDetail=""
                isOpen={accordionOpened.grantLevel}
                onClick={() => handleAccordionClick("grantLevel")}
                numberOfItems={grantDocs.length}
              >
                <VStack className="gap-2 mt-1">
                  {grantDocs.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      No documents
                    </p>
                  )}
                  {grantDocs.map((doc) =>
                    renderDocCard(doc, !doc.isUserUploaded)
                  )}
                  {renderAddButton("grant")}
                </VStack>
              </CollapsibleComponent>

              {/* Employee-Level Documents */}
              <CollapsibleComponent
                label="Employee-Level Documents"
                labelDetail=""
                isOpen={accordionOpened.employeeLevel}
                onClick={() => handleAccordionClick("employeeLevel")}
                numberOfItems={employeeDocs.length}
              >
                <VStack className="gap-2 mt-1">
                  {employeeDocs.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      No documents
                    </p>
                  )}
                  {employeeDocs.map((doc) =>
                    renderDocCard(doc, !doc.isUserUploaded)
                  )}
                  {renderAddButton("employee")}
                </VStack>
              </CollapsibleComponent>

              {/* Transaction Documents */}
              <CollapsibleComponent
                label="Transaction Documents"
                labelDetail=""
                isOpen={accordionOpened.transactionDocs}
                onClick={() => handleAccordionClick("transactionDocs")}
                numberOfItems={transactionDocs.length}
              >
                <VStack className="gap-2 mt-1">
                  {transactionDocs.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      No documents — upload exercise, buyback or sold documents
                      here
                    </p>
                  )}
                  {transactionDocs.map((doc) =>
                    renderDocCard(doc, !doc.isUserUploaded)
                  )}
                  {renderAddButton("transaction")}
                </VStack>
              </CollapsibleComponent>
            </VStack>
          </div>
        </div>

        {/* ── Right panel: preview or upload form ── */}
        <div className="w-[60%] flex flex-col h-full">
          {rightPanelMode === "preview" ? (
            <>
              {/* Preview header */}
              <div className="px-6 py-5 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedDoc
                      ? getDocumentName(selectedDoc, grant) || "Untitled"
                      : "Preview"}
                  </h2>
                  {selectedDoc && (
                    <button
                      onClick={() => downloadFile(selectedDoc)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                    >
                      <Icon icon="mdi:download" width={18} />
                      Download
                    </button>
                  )}
                </div>
                {selectedDoc && (
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                    <span>
                      Generated On:{" "}
                      {selectedDoc.generatedOn
                        ? formatDisplayDate(selectedDoc.generatedOn)
                        : "—"}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {selectedDoc.isUserUploaded
                        ? "User-Uploaded"
                        : "System-Generated"}
                    </span>
                    {selectedDoc.metaData?.shownToEmployee && (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                        Visible to Employee
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Preview body */}
              <div
                className="flex-1 overflow-y-auto p-8 bg-gray-50"
                ref={previewRef}
              >
                {renderPreview()}
              </div>
            </>
          ) : (
            /* Upload / Edit form */
            <div className="flex-1 overflow-y-auto">{renderUploadForm()}</div>
          )}
        </div>
      </HStack>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="md"
      >
        <AlertDialog
          onClose={() => setDeleteDialog({ open: false })}
          open={deleteDialog.open}
          message="Do you want to delete this document?"
          onPrimaryAction={confirmDelete}
          onSecondaryAction={() => setDeleteDialog({ open: false })}
        />
      </Dialog>
    </VStack>
  );
}
